import type { BuildContext, Fragment } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { beforeAfterSchema, type BeforeAfterProps } from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { tl } from "../timeline.js";
import { assemble } from "./shared.js";

export const BEFORE_AFTER_DURATION_SEC = 9;

export function buildBeforeAfterHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = beforeAfterSchema.parse(rawProps) as BeforeAfterProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: BEFORE_AFTER_DURATION_SEC };

  const titleSize = scaleFor(ctx.format, { base: 56, short: 60, square: 50, wide: 56 });
  const metricSize = scaleFor(ctx.format, { base: 180, short: 220, square: 160, wide: 200 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  const stacked = ctx.format === "9:16";

  const beforeStart = 4 / 30;
  const beforeDur = BEFORE_AFTER_DURATION_SEC - beforeStart;
  const afterStart = 45 / 30;
  const afterDur = BEFORE_AFTER_DURATION_SEC - afterStart;
  const ctaStart = 210 / 30;
  const ctaDur = 70 / 30;

  const bg = backgroundGradient(fullCtx, "bg", { variant: "dark", animated: true });
  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });
  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const beforeHook = hookText({
    id: "tpl-before-title",
    text: props.before,
    startSec: beforeStart,
    size: titleSize,
    color: "#fff",
    align: "center",
  });
  const afterHook = hookText({
    id: "tpl-after-title",
    text: props.after,
    startSec: afterStart,
    size: titleSize,
    color: "#fff",
    align: "center",
  });

  const renderSide = (
    id: string,
    label: string,
    metric: string,
    accent: string,
    titleHtml: string,
  ): string => `<div id="${id}" style="flex:1;background:rgba(255,255,255,0.04);border:2px solid ${accent}55;border-radius:36px;padding:56px;display:flex;flex-direction:column;gap:18px;justify-content:center;align-items:center;text-align:center;">
  <div style="font-weight:800;font-size:32px;letter-spacing:4px;text-transform:uppercase;color:${accent};">${escapeHtml(label)}</div>
  ${metric ? `<div style="font-weight:900;font-size:${metricSize}px;color:white;line-height:1;letter-spacing:-4px;">${escapeHtml(metric)}</div>` : ""}
  ${titleHtml}
</div>`;

  const beforeSide = renderSide("tpl-before-side", loc.labels.before, props.beforeMetric, "#fca5a5", beforeHook.html);
  const afterSide = renderSide("tpl-after-side", loc.labels.after, props.afterMetric, colors.accent, afterHook.html);

  // Side reveal: fade+slide in at the configured start.
  const sideInit = `gsap.set("#tpl-before-side", { opacity: 0, y: 30 }); gsap.set("#tpl-after-side", { opacity: 0, y: 30 });`;
  const sideTweens = [
    tl.fromTo(
      "#tpl-before-side",
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
      beforeStart,
    ),
    tl.fromTo(
      "#tpl-after-side",
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
      afterStart,
    ),
  ];

  const sideFragment: Fragment = {
    html: "",
    init: sideInit,
    tweens: sideTweens,
  };

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div id="tpl-cmp" style="flex:1;display:flex;flex-direction:${stacked ? "column" : "row"};gap:32px;align-items:stretch;justify-content:center;">
  <div id="tpl-before-clip" class="clip" data-start="${beforeStart}" data-duration="${beforeDur}" data-track-index="0" style="display:flex;flex:1;">${beforeSide}</div>
  <div id="tpl-after-clip" class="clip" data-start="${afterStart}" data-duration="${afterDur}" data-track-index="1" style="display:flex;flex:1;">${afterSide}</div>
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="2" style="display:flex;justify-content:center;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "BeforeAfter",
    ctx: fullCtx,
    background: bg,
    contentFragments: [logoFrag, beforeHook, afterHook, sideFragment, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
